import React, { useEffect, useState } from "react";
import { Container, Row, Col, Modal, Button, Form } from "react-bootstrap";
import api from "../api";
import EventDetails from "./EventDetails";
import EventSidebar from "./EventSidebar";
import EventUrlsCard from "./EventUrlsCard";
import EventStatsCard from "./EventStatsCard";
import SocialStats from "./SocialStats";
import EventPhotosStats from "./EventPhotosStats";
import NewEventModal from "./NewEventModal";
import { useAuth } from "../AuthContext";
import { useTranslation } from "react-i18next";

const EventsAdmin = ({ onLogout, user, selectedE }) => {
  const { t } = useTranslation();
  const { events, setEvents } = useAuth();

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [newEvent, setNewEvent] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [typeList, setTypeList] = useState([
    { type_id: 1, type_name: t("events_admin.types.1") },
    { type_id: 2, type_name: t("events_admin.types.2") },
    { type_id: 3, type_name: t("events_admin.types.3") },
    { type_id: 4, type_name: t("events_admin.types.4") },
    { type_id: 5, type_name: t("events_admin.types.5") },
    { type_id: 6, type_name: t("events_admin.types.6") },
    { type_id: 7, type_name: t("events_admin.types.7") }
  ]);

  const getEmptyEvent = () => ({
    name: "",
    id_name: "",
    date: "",
    city: "",
    country: "",
    type_id: "",
    event_room: "",
    user_id: user?.user_id ?? null,
  });

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/events?user_id=${user.user_id}`, {
        withCredentials: true,
      });
      setEvents(res.data);

      if (res.data.length > 0) {
        setSelectedEvent(res.data[0]);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  const removeEvent = async (event) => {
    if (window.confirm(t("events_admin.confirm_delete", { name: event.name }))) {
      try {
        await api.delete(`/event/${event.event_id}`, { withCredentials: true });
        setEvents(events.filter((ev) => ev.event_id !== event.event_id));
        setSelectedEvent(null);
      } catch (error) {
        console.error("Error deleting event:", error);
      }
    }
  };

  useEffect(() => {
    if (user?.user_id && events.length > 0) setSelectedEvent(events[0] || null);
  }, [user]);

  useEffect(() => {
    if (selectedE) {
      const event = events.find((ev) => ev.id_name === selectedE);
      if (event) setSelectedEvent(event);
    }
  }, [selectedE, events]);

  const filteredEvents = events.filter((ev) =>
    ev.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectEvent = (ev) => {
    setSelectedEvent(ev);
    setSidebarOpen(false);
  };

  const handleCreateEvent = async (eventData) => {
    try {
      await api.post(`/event`, eventData, { withCredentials: true });
      setNewEvent(null);
      fetchEvents();
    } catch (err) {
      console.error("Failed to create event", err);
      alert("Creation failed");
    }
  };

  return (
    <Container fluid className="mt-3">

      {/* Mobile toggle bar */}
      <div
        className="d-md-none d-flex align-items-center justify-content-between px-3 py-2 mb-2"
        style={{ background: "#fff", borderBottom: "1.5px solid #e0e0e0", cursor: "pointer" }}
        onClick={() => setSidebarOpen((o) => !o)}
      >
        <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "#111", letterSpacing: "0.01em" }}>
          {sidebarOpen ? "✕  Close" : "☰  Events"}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); setNewEvent(getEmptyEvent()); }}
          style={{
            fontSize: "0.75rem",
            fontWeight: 600,
            color: "#6366f1",
            background: "#eef2ff",
            border: "1px solid #c7d2fe",
            borderRadius: "6px",
            padding: "4px 12px",
            cursor: "pointer",
          }}
        >
          + New
        </button>
      </div>

      <Row>
        {/* Sidebar — always visible on md+, collapsible on mobile */}
        <Col
          md={3}
          className={`border-end ${sidebarOpen ? "d-block" : "d-none d-md-block"}`}
        >
          <EventSidebar
            events={filteredEvents}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onSelectEvent={handleSelectEvent}
            selectedEvent={selectedEvent}
            onNewEvent={() => setNewEvent(getEmptyEvent())}
          />
        </Col>

        {selectedEvent ? (
          <Col md={9} className={`p-4 ${sidebarOpen ? "d-none d-md-block" : ""}`}>
            {loading ? (
              <div>{t("events_admin.loading")}</div>
            ) : (
              <>
                {selectedEvent && (
                  <h4 className="mb-3 text-dark fw-bold">
                    {selectedEvent.name}
                  </h4>
                )}

                <EventUrlsCard
                  urls={{
                    gallery: `${process.env.REACT_APP_BASE_URL}/event/${selectedEvent?.id_name}/gallery`,
                    magicFinder: `${process.env.REACT_APP_BASE_URL}/event/${selectedEvent?.id_name}`,
                    social: `${process.env.REACT_APP_BASE_URL}/event/${selectedEvent?.id_name}/social`,
                    qr: `${process.env.REACT_APP_MODEL_ENDPOINT}/qr?eid=${selectedEvent?.event_id}`,
                    qrSocial: `${process.env.REACT_APP_MODEL_ENDPOINT}/qr?eid=${selectedEvent?.event_id}&type=social`,
                  }}
                />

                <EventPhotosStats
                  id_name={selectedEvent?.id_name}
                  storage={selectedEvent?.storage}
                />

                <EventStatsCard stats={selectedEvent?.stats || {}} />
                <SocialStats stats={selectedEvent?.social_stats || {}} />

                <EventDetails
                  key={selectedEvent?.event_id}
                  event={selectedEvent}
                  typeList={typeList}
                  onEdit={setEditingEvent}
                  onDelete={removeEvent}
                  onCoverUpdate={(url) =>
                    setSelectedEvent((ev) => ({ ...ev, cover_image_url: url }))
                  }
                />
              </>
            )}
          </Col>
        ) : (
          <div className={`text-center text-muted py-5 ${sidebarOpen ? "d-none d-md-block" : ""}`}>
            {events?.length === 0 ? (
              <>
                <p className="mb-3 fs-5">{t("events_admin.empty_create")}</p>
                <Button variant="outline-secondary" onClick={() => setNewEvent(getEmptyEvent())}>
                  {t("events_admin.new_event")}
                </Button>
              </>
            ) : (
              <p className="mb-0 fs-5">{t("events_admin.empty_select")}</p>
            )}
          </div>
        )}
      </Row>

      {/* Edit Modal */}
      <Modal show={!!editingEvent} onHide={() => setEditingEvent(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{t("events_admin.edit_title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingEvent && (
            <Form>
              <Form.Group className="mb-2">
                <Form.Label>{t("events_admin.name")}</Form.Label>
                <Form.Control
                  value={editingEvent.name}
                  onChange={(e) =>
                    setEditingEvent({ ...editingEvent, name: e.target.value })
                  }
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>{t("events_admin.city")}</Form.Label>
                <Form.Control
                  value={editingEvent.city}
                  onChange={(e) =>
                    setEditingEvent({ ...editingEvent, city: e.target.value })
                  }
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>{t("events_admin.country")}</Form.Label>
                <Form.Control
                  value={editingEvent.country}
                  onChange={(e) =>
                    setEditingEvent({ ...editingEvent, country: e.target.value })
                  }
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>{t("events_admin.date")}</Form.Label>
                <Form.Control
                  type="date"
                  value={editingEvent.date}
                  onChange={(e) =>
                    setEditingEvent({ ...editingEvent, date: e.target.value })
                  }
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>{t("events_admin.venue")}</Form.Label>
                <Form.Control
                  value={editingEvent.event_room || ""}
                  onChange={(e) =>
                    setEditingEvent({
                      ...editingEvent,
                      event_room: e.target.value,
                    })
                  }
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>{t("events_admin.type")}</Form.Label>
                <Form.Select
                  value={editingEvent.type_id || ""}
                  onChange={(e) =>
                    setEditingEvent({
                      ...editingEvent,
                      type_id: parseInt(e.target.value),
                    })
                  }
                >
                  <option value="">{t("events_admin.select_type")}</option>
                  {typeList.map((type) => (
                    <option key={type.type_id} value={type.type_id}>
                      {type.type_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditingEvent(null)}>
            {t("events_admin.btn_cancel")}
          </Button>

          <Button
            variant="primary"
            onClick={async () => {
              await api.put(`/event/${editingEvent.event_id}`, editingEvent, {
                withCredentials: true,
              });
              setEditingEvent(null);
              fetchEvents();
            }}
          >
            {t("events_admin.btn_save")}
          </Button>
        </Modal.Footer>
      </Modal>

      <NewEventModal
        show={!!newEvent}
        onHide={() => setNewEvent(null)}
        onCreate={handleCreateEvent}
        user={user}
        typeList={typeList}
      />
    </Container>
  );
};

export default EventsAdmin;
